// M17 Vorlagen-Galerie: Branchenvorlagen (E9) mit Kategorien, Suche, grosser Vorschau und „Vorlage verwenden“,
// dazu Basisvorlagen (Zonen) und eigene Vorlagen mit Filter Quer-/Hochformat, Duplizieren, Designer öffnen.
import { useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ApiError, api } from '../api/client';
import { can, qk, useLayouts, useMe, useTemplateCatalog } from '../api/queries';
import type { LayoutRow, TemplateCategory } from '../api/types';
import { usePendingMap } from '../api/approvals';
import { PendingPill } from '../components/Approval';
import { Icon } from '../components/Icon';
import { useMediaIndex, type MediaIndex } from '../components/Preview';
import { useToast } from '../components/toast';
import { Confirm, Dialog, Empty, Field, PageHead, Spinner } from '../components/ui';
import { previewPath } from '../preview/open';
import { TemplatePreview } from './PlaylistEditor';
import './templates/gallery.css';
import { FormatSwitch, TemplateDetailDialog, TemplateFrame, UseTemplateDialog } from './templates/TemplateDialogs';
import { groupCatalog, matches, tr, type TemplateGroup } from './templates/shared';
import { EditCopyButton } from './template-edit/TemplateEdit';
import { categoryAllowed, disabledWidgets } from '../api/features';

type Orientation = 'landscape' | 'portrait';
/** Filter: Branche, "basic" (Zonenvorlagen und Prototyp-Vorlagen), "own" (eigene) oder alle. */
type Cat = string;

export function TemplatesPage() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const { data: me } = useMe();
  const { data: layouts, isLoading } = useLayouts();
  const { data: catalog } = useTemplateCatalog();
  const media = useMediaIndex();
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const cat: Cat = params.get('kategorie') ?? '';
  const setCat = (c: Cat) => setParams((p) => { const n = new URLSearchParams(p); if (c) n.set('kategorie', c); else n.delete('kategorie'); return n; }, { replace: true });
  const [orient, setOrient] = useState<'' | Orientation>('');
  const [q, setQ] = useState('');
  const [create, setCreate] = useState(false);
  const [del, setDel] = useState<LayoutRow | null>(null);
  const [detail, setDetail] = useState<{ group: TemplateGroup; o: Orientation } | null>(null);
  const [use, setUse] = useState<{ group: TemplateGroup; o: Orientation } | null>(null);
  const manage = can(me, 'layouts.manage');
  const pending = usePendingMap();
  const isV2 = (l: LayoutRow) => (l.definition as { specVersion?: number }).specVersion === 2;

  const groups = useMemo(() => groupCatalog(catalog), [catalog]);
  const catByKey = useMemo(() => new Map((catalog?.categories ?? []).map((c) => [c.key, c])), [catalog]);
  const industryIds = useMemo(() => new Set((catalog?.items ?? []).map((i) => i.layoutId)), [catalog]);
  const shownGroups = groups.filter((g) => (!cat || cat === g.category) && (!orient || g.variants[orient])
    && matches(g, q, catByKey.get(g.category) ? tr(catByKey.get(g.category)!.name, lang) : ''));
  const qn = q.trim().toLowerCase();
  const others = (layouts ?? []).filter((l) => !industryIds.has(l.id));
  const list = others.filter((l) => (!orient || l.orientation === orient) && (!qn || l.name.toLowerCase().includes(qn))
    && (!cat || (cat === 'own' ? l.scope === 'tenant' : cat === 'basic' ? l.scope === 'global' : false)));
  const count = (c: Cat) => (c === 'own' ? others.filter((l) => l.scope === 'tenant').length : c === 'basic' ? others.filter((l) => l.scope === 'global').length
    : groups.filter((g) => g.category === c).length);
  const chip = (key: Cat, label: string, color?: string) => (
    <button key={key || 'all'} className={`tg-chip${cat === key ? ' on' : ''}`} aria-pressed={cat === key} onClick={() => setCat(key)}
      style={color ? { ['--c' as string]: color } : undefined}>
      {color && <span className="dot" />}{label}{key && <span className="n">{count(key)}</span>}
    </button>
  );

  const duplicate = async (l: LayoutRow) => {
    try {
      const copy = await api.post<LayoutRow>('/layouts', { name: t('templates.copyOf', { name: l.name }), basedOnLayoutId: l.id });
      await qc.invalidateQueries({ queryKey: qk.layouts });
      toast.show(t('templates.duplicated'));
      if (isV2(copy)) navigate(`/studio/${copy.id}`);
    } catch (e) {
      toast.error(e);
    }
  };

  return (
    <>
      <PageHead title={t('nav.templates')} sub={t('templates.sub')}
        actions={manage && <button className="btn primary" onClick={() => setCreate(true)}><Icon name="plus" />{t('templates.new')}</button>} />
      <div className="tg-chips" role="group" aria-label={t('gallery.category')}>
        {chip('', t('common.all'))}
        {(catalog?.categories ?? []).map((c) => chip(c.key, tr(c.name, lang), c.color))}
        {(categoryAllowed(me, 'basis') || count('basic') > 0) && chip('basic', t('gallery.basic'))}
        {chip('own', t('templates.own'))}
      </div>
      <div className="toolbar">
        <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('gallery.search')} aria-label={t('gallery.search')} />
        <div className="seg" role="group" aria-label={t('templates.format')}>
          {(['', 'landscape', 'portrait'] as const).map((o) => (
            <button key={o || 'all'} className={orient === o ? 'on' : ''} aria-pressed={orient === o} onClick={() => setOrient(o)}>
              {o === '' ? t('common.all') : o === 'landscape' ? t('design.landscape') : t('design.portrait')}
            </button>
          ))}
        </div>
      </div>
      {isLoading ? <Spinner /> : !list.length && !shownGroups.length ? (
        <div className="card"><Empty icon="layout" title={t('templates.empty')} /></div>
      ) : null}
      {!isLoading && shownGroups.length > 0 && (
        <section aria-label={t('gallery.industry')} style={{ marginBottom: 28 }}>
          {!cat && <h2>{t('gallery.industry')}</h2>}
          <div className="tg-grid" data-testid="template-gallery">
            {shownGroups.map((g) => (
              <GroupCard key={`${g.group}-${orient}`} group={g} category={catByKey.get(g.category)} layouts={layouts ?? []} media={media} prefer={orient || 'landscape'}
                canUse={manage} onOpen={(o) => setDetail({ group: g, o })} onUse={(o) => setUse({ group: g, o })} />
            ))}
          </div>
        </section>
      )}
      {!isLoading && list.length > 0 && (
        <section aria-label={t('gallery.more')}>
        {!cat && <h2>{t('gallery.more')}</h2>}
        <div className="grid-cards">
          {list.map((l) => (
            <div key={l.id} className="card tpl" style={{ padding: 12 }}>
              <TemplatePreview layout={l} media={media} />
              <div className="row" style={{ gap: 6 }}>
                <b className="grow" title={l.name}>{l.name}</b>
                <span className={`pill plain ${l.scope === 'global' ? 'info' : 'neutral'}`}>{l.scope === 'global' ? t('templates.global') : t('templates.own')}</span>
              </div>
              {pending.has(l.id) && <div><PendingPill request={pending.get(l.id)} /></div>}
              {l.scope === 'tenant' && disabledWidgets(me, l.definition).length > 0 && (
                <div className="hint warn small" title={t('features.layoutHintLong')}>
                  {t('features.layoutHint', { list: disabledWidgets(me, l.definition).map((w) => t(`widget.${w}`)).join(', ') })}
                </div>
              )}
              <div className="muted small">
                {l.orientation === 'portrait' ? '9:16' : '16:9'} · {isV2(l) ? t('templates.free') : t('templates.zones')}
                {l.scope === 'tenant' && (l.latestRevisionNo ? ` · ${t('studio.revision', { no: l.latestRevisionNo })}${l.draftChanged ? ` · ${t('studio.unsaved')}` : ''}` : ` · ${t('studio.notPublished')}`)}
              </div>
              <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
                {manage && l.scope === 'tenant' && isV2(l) && <button className="btn sm primary" onClick={() => navigate(`/studio/${l.id}`)}><Icon name="edit" />{t('templates.open')}</button>}
                <a className="btn sm" href={previewPath('layout', l.id)} target="_blank" rel="noopener" title={t('preview.openHint')}><Icon name="play" />{t('preview.open')}</a>
                {manage && <>
                  {l.scope === 'global' || !isV2(l) ? <EditCopyButton layout={l} small />
                    : <button className="btn sm" onClick={() => void duplicate(l)}><Icon name="copy" />{t('templates.duplicate')}</button>}
                  {l.scope === 'tenant' && <button className="btn sm ghost danger icon" aria-label={t('common.delete')} title={t('common.delete')} onClick={() => setDel(l)}><Icon name="trash" /></button>}
                </>}
              </div>
            </div>
          ))}
        </div>
        </section>
      )}
      {detail && (
        <TemplateDetailDialog group={detail.group} category={catByKey.get(detail.group.category)} layouts={layouts ?? []} media={media} initial={detail.o}
          onClose={() => setDetail(null)} onUse={(o) => { setDetail(null); setUse({ group: detail.group, o }); }} />
      )}
      {use && <UseTemplateDialog group={use.group} orientation={use.o} onClose={() => setUse(null)} />}
      {create && <NewLayoutDialog onClose={() => setCreate(false)} onCreated={(id) => navigate(`/studio/${id}`)} />}
      {del && (
        <Confirm danger title={t('common.delete')} text={t('templates.confirmDelete', { name: del.name })} confirmLabel={t('common.delete')} onClose={() => setDel(null)}
          onConfirm={async () => {
            try {
              await api.del(`/layouts/${del.id}`, { ifMatch: del.rowVersion });
              await qc.invalidateQueries({ queryKey: qk.layouts });
            } catch (e) {
              if (e instanceof ApiError && e.status === 409 && e.problem.errors?.playlists) toast.show(t('templates.inUse', { list: e.problem.errors.playlists.join(', ') }));
              else toast.error(e);
            }
            setDel(null);
          }} />
      )}
    </>
  );
}

function NewLayoutDialog({ onClose, onCreated }: { onClose: () => void; onCreated: (id: string) => void }) {
  const { t } = useTranslation();
  const toast = useToast();
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [orientation, setOrientation] = useState<'landscape' | 'portrait'>('landscape');
  const submit = async () => {
    try {
      const l = await api.post<LayoutRow>('/layouts', { name: name.trim(), orientation });
      await qc.invalidateQueries({ queryKey: qk.layouts });
      onCreated(l.id);
    } catch (e) {
      toast.error(e);
    }
  };
  return (
    <Dialog title={t('templates.new')} onClose={onClose} actions={<>
      <button className="btn ghost" onClick={onClose}>{t('common.cancel')}</button>
      <button className="btn primary" disabled={!name.trim()} onClick={submit}>{t('common.create')}</button>
    </>}>
      <form className="col" onSubmit={(e) => { e.preventDefault(); if (name.trim()) void submit(); }}>
        <Field label={t('common.name')}><input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder={t('studio.namePh')} /></Field>
        <div className="field">{t('templates.format')}
          <div className="row">
            <label className="check"><input type="radio" name="o" checked={orientation === 'landscape'} onChange={() => setOrientation('landscape')} />{t('design.landscape')}</label>
            <label className="check"><input type="radio" name="o" checked={orientation === 'portrait'} onChange={() => setOrientation('portrait')} />{t('design.portrait')}</label>
          </div>
        </div>
      </form>
    </Dialog>
  );
}

/** Karte einer Branchenvorlage (16:9 und 9:16 umschaltbar). */
function GroupCard({ group, category, layouts, media, prefer, canUse, onOpen, onUse }: {
  group: TemplateGroup; category: TemplateCategory | undefined; layouts: LayoutRow[]; media: MediaIndex; prefer: Orientation; canUse: boolean;
  onOpen: (o: Orientation) => void; onUse: (o: Orientation) => void;
}) {
  const { t, i18n } = useTranslation();
  const [o, setO] = useState<Orientation>(group.variants[prefer] ? prefer : group.variants.landscape ? 'landscape' : 'portrait');
  const shown: Orientation = group.variants[o] ? o : group.variants.landscape ? 'landscape' : 'portrait';
  const item = group.variants[shown]!;
  const layout = layouts.find((l) => l.id === item.layoutId);
  const name = tr(group.name, i18n.language);
  return (
    <div className="card tg-card" data-testid="template-card">
      <button className="tg-thumb" onClick={() => onOpen(shown)} aria-label={t('gallery.show', { name })}>
        <TemplateFrame layout={layout} media={media} orientation={shown} />
      </button>
      <div className="row" style={{ gap: 6 }}>
        {category && <span className="tg-cat grow" style={{ ['--c' as string]: category.color }}>{tr(category.name, i18n.language)}</span>}
        <FormatSwitch group={group} value={shown} onChange={setO} small />
      </div>
      <b>{name}</b>
      <div className="muted small desc">{tr(group.description, i18n.language)}</div>
      <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
        {canUse && <button className="btn sm primary" onClick={() => onUse(shown)}><Icon name="check" />{t('gallery.use')}</button>}
        {canUse && <EditCopyButton layout={{ id: item.layoutId, name }} small />}
        <button className="btn sm" onClick={() => onOpen(shown)}><Icon name="fullscreen" />{t('gallery.view')}</button>
      </div>
    </div>
  );
}
